/**
 * ReinstateOrgDialog — single-phase confirm for `POST
 * /api/platform/orgs/:orgId/reinstate` (slice 3 — see
 * `ai-docs/deleted-orgs-reinstate-handoff.md`).
 *
 * Flips a soft-deleted org DELETED → ACTIVE + restarts every
 * soft-stopped runtime in place + rebuilds routing. The action is
 * non-destructive (it RECOVERS state), so this is just a reason
 * prompt — no two-phase token, no type-to-confirm phrase. The
 * server validates `reason` length (3-500 chars).
 *
 * Per-runtime outcomes from the response (`reinstated` / `skipped` /
 * `failed`) are surfaced by the parent via the success toast — this
 * dialog returns the operator's `reason` and closes.
 */

import { useEffect, useState } from "react";
import { Play } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const REASON_MIN = 3;
const REASON_MAX = 500;

export interface ReinstateOrgDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Org being reinstated; copy includes both for operator clarity. */
  orgName: string;
  orgSlug: string;
  /** Parent runs the API call + handles loading/error. */
  onConfirm: (reason: string) => Promise<void> | void;
  /** Set while the parent's API call is in flight. */
  busy?: boolean;
  /** Parent-surfaced error (e.g. INVALID_TRANSITION if the org isn't DELETED). */
  error?: string | null;
}

export function ReinstateOrgDialog({
  open,
  onOpenChange,
  orgName,
  orgSlug,
  onConfirm,
  busy = false,
  error = null,
}: ReinstateOrgDialogProps) {
  const [reason, setReason] = useState("");

  // Reset on (re)open so a previous attempt's text doesn't carry over.
  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  const trimmed = reason.trim();
  const reasonOk = trimmed.length >= REASON_MIN && trimmed.length <= REASON_MAX;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg space-y-4"
        data-testid="deleted-orgs-reinstate-dialog"
      >
        <DialogHeader className="items-center sm:text-center">
          <DialogTitle className="flex items-center justify-center gap-2 text-2xl text-foreground">
            <Play className="h-6 w-6 text-primary" />
            Reinstate organization
          </DialogTitle>
          <DialogDescription>
            Recovers{" "}
            <strong className="font-mono text-foreground">{orgSlug}</strong>{" "}
            ({orgName}) — every soft-stopped runtime is restarted in
            place and routing is rebuilt from each tombstone's snapshot.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <label
            htmlFor="deleted-orgs-reinstate-reason"
            className="text-xs font-medium text-muted"
          >
            Reason (audited; {REASON_MIN}-{REASON_MAX} chars):
          </label>
          <textarea
            id="deleted-orgs-reinstate-reason"
            data-testid="deleted-orgs-reinstate-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Why is this org being reinstated?"
            disabled={busy}
            className="w-full rounded-md border border-border bg-panel-2 px-3 py-2 text-sm text-foreground outline-none placeholder:text-faint focus:border-primary focus:ring-1 focus:ring-primary/30 disabled:opacity-60"
          />
        </div>

        {error && (
          <div
            className="rounded-md border border-critical/40 bg-critical/10 p-3 text-sm text-critical"
            data-testid="deleted-orgs-reinstate-error"
          >
            {error}
          </div>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="deleted-orgs-reinstate-cancel"
            className="rounded-md border border-border bg-panel-2 px-3 py-1.5 text-sm font-medium text-muted transition-colors hover:bg-accent hover:text-foreground disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!reasonOk || busy}
            onClick={() => void onConfirm(trimmed)}
            data-testid="deleted-orgs-reinstate-confirm"
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy ? "Reinstating…" : "Reinstate"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
